/** @jsxImportSource theme-ui */
import { useBreakpointIndex } from '@theme-ui/match-media';
import { parseISO } from 'date-fns';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Card } from 'semantic-ui-react';

import * as dto from 'client/dto';
import { BdScrubberModel } from 'models/BdScrubberModel';
import { AggregationCat } from 'models/DiseaseModels';
import { AggregationThresholdsDetails } from 'utils/constants';
import { sxtheme } from 'utils/cssHelpers';
import { getCurrentUtcDate, getMomentAgo, parseIsoDateStartOfDayUtc0 } from 'utils/dateTimeHelpers';
import { ComputeAggregationCategory, MapAggregationCat2AggregationDays } from 'utils/modelHelpers';
import { isMobile } from 'utils/responsive';
import { formatNumber } from 'utils/stringFormattingHelpers';

import { BdIcon } from 'components/_common/BdIcon';
import { FlexGroup } from 'components/_common/FlexGroup';
import { BdCardFooterLink } from 'components/_common/SemanticAddons/BdCardFooterLink';
import { Typography } from 'components/_common/Typography';
import { BdScrubber } from 'components/_controls/BdScrubber';
import {
  DiseaseActivityDatumType,
  DiseaseActivityDatumUpdate,
} from 'components/_graphs/_common/DiseaseActivityModels';
import { BdDiseaseActivityGraph } from 'components/_graphs/BdDiseaseActivityGraph/BdDiseaseActivityGraph';

import diseaseActivitiesTimeFrameSelectors from 'diseaseActivitiesTimeFrame/diseaseActivitiesTimeFrame.selectors';
import { DaGraphLegend } from 'diseaseActivityPanels/components/DiseaseActivityCards/DaGraphLegend';

import {
  DiseaseActivityPopupAllUpdates,
  DiseaseActivityPopupCaseCounts,
  DiseaseActivityPopupFooterNegativeValues,
  DiseaseActivityPopupHeaderDate,
  DiseaseActivityPopupUpdatesPreview,
} from './DiseaseActivityCardPopupComponents';

// =====================================================================================================================================
const GraphWidth = 275;
const GraphPaddingR = 25;
const GraphMarginPopoutHackPx = 15;
const GraphMarginChipsAlignYAxisCutoffHackPx = 7;
const DateToday = getCurrentUtcDate();
const Date30DaysAgo = getMomentAgo('last-30-days').toDate();
const Date30DaysInMilleseconds = 1000 * 60 * 60 * 24 * 30;
// =====================================================================================================================================

export type GetDiseaseActivityApiFunction = (
  startDate: Date,
  endDate: Date,
  aggregationCategory: AggregationCat
) => Promise<dto.TemporalActivityModel[]>;

type DiseaseActivityCardProps = {
  isGlobal?: boolean;
  countryName?: string;
  totalCaseCounts?: dto.CaseCountModel;
  firstActivityUpdateDate?: string;
  isFooterActive: boolean;
  onNeedApiGetDiseaseActivity: GetDiseaseActivityApiFunction;
  onGoToActivityHistory: (activityDate?: string) => void;
};

const DiseaseActivityCard: React.FC<DiseaseActivityCardProps> = ({
  isGlobal = false,
  countryName,
  totalCaseCounts,
  firstActivityUpdateDate,
  isFooterActive,
  onNeedApiGetDiseaseActivity,
  onGoToActivityHistory,
}) => {
  const startDate = useSelector(diseaseActivitiesTimeFrameSelectors.getStartDate);

  const isMobileDevice = isMobile(useBreakpointIndex());

  const [aggregationCategoryState, setAggregationCategoryState] = useState<AggregationCat>('day');
  const [showAverageCurveInLegend, setShowAverageCurveInLegend] = useState(false);
  const [graphData, setGraphData] = useState<DiseaseActivityDatumType[]>(null);
  const [isGraphLoading, setIsGraphLoading] = useState(true);

  const temporalWindowStartDate = useMemo(() => parseISO(startDate), [startDate]);
  const temporalWindowEndDate = useMemo(() => new Date(), []);

  const labelCases = isGlobal ? 'Global reported cases' : 'Reported cases';
  const labelDeaths = isGlobal ? 'Global reported deaths' : 'Reported deaths';
  const labelCardTitle = isGlobal ? 'Global activity' : `Activity in ${countryName}`;

  const renderGraphPopupLower = useCallback(
    (datum: DiseaseActivityDatumType, isActive: boolean, callbackClosePopup: () => void) => (
      <div data-testid="DiseaseActivityCardPopupUpdateSection">
        <DiseaseActivityPopupHeaderDate
          datum={datum}
          showCloseButton={isActive}
          onClose={callbackClosePopup}
        />
        <DiseaseActivityPopupCaseCounts
          labelSeries1={labelCases}
          valueSeries1={!datum.isGhost && datum.series1}
          labelSeries2={labelDeaths}
          valueSeries2={!datum.isGhost && datum.series2}
        />
        {!isActive && datum.updatesCount > 0 && datum.updates?.length > 0 && (
          <DiseaseActivityPopupUpdatesPreview topUpdate={datum.updates[0]} />
        )}
        {isActive && (
          <DiseaseActivityPopupAllUpdates
            topUpdates={datum.updates}
            totalUpdatesCount={datum.updatesCount}
            callbackClosePopup={callbackClosePopup}
            onGoToActivityHistory={onGoToActivityHistory}
          />
        )}
        {(datum.series1 < 0 || datum.series2 < 0) && <DiseaseActivityPopupFooterNegativeValues />}
      </div>
    ),
    [labelCases, labelDeaths]
  );
  const renderGraphPopupUpper = useCallback(
    (datum: DiseaseActivityDatumType) => (
      <FlexGroup alignItems="flex-start" prefix={<BdIcon name="icon-information" color="white" />}>
        <Typography variant="caption2" color="white">
          {formatNumber(datum.updatesCount, 'update', 'updates', 'emdash-0')}
        </Typography>
        <Typography variant="label-small" color="white">
          Click bar to view
        </Typography>
      </FlexGroup>
    ),
    []
  );

  // Scrubber values
  const scrubberMinValue = useMemo(
    () =>
      firstActivityUpdateDate
        ? moment
            .min(moment(parseIsoDateStartOfDayUtc0(firstActivityUpdateDate)), moment(Date30DaysAgo))
            .toDate()
        : Date30DaysAgo,
    [firstActivityUpdateDate]
  );

  const scrubberMaxValue = useMemo(() => DateToday, []);
  const [selectedScrubberValue, setSelectedScrubberValue] = useState<BdScrubberModel>({
    startValue: scrubberMinValue.getTime(),
    endValue: scrubberMaxValue.getTime(),
  });

  const [releasedScrubberValue, setReleasedScrubberValue] = useState<BdScrubberModel>({
    startValue: scrubberMinValue.getTime(),
    endValue: scrubberMaxValue.getTime(),
  });

  const scrubberSetup = {
    min: scrubberMinValue.getTime(),
    max: scrubberMaxValue.getTime(),
    tipFormatter: val => moment(new Date(val)).format('MMM DD, YYYY'),
    disabled: scrubberMinValue.getTime() >= Date30DaysAgo.getTime(),
    minLabel: moment(scrubberMinValue).format('MMM YYYY').toUpperCase(),
    maxLabel: moment(scrubberMaxValue).format('MMM YYYY').toUpperCase(),
  };

  const aggregationCategory = useMemo(
    () =>
      ComputeAggregationCategory({
        startDate: new Date(releasedScrubberValue.startValue),
        endDate: new Date(releasedScrubberValue.endValue),
        thresholds: AggregationThresholdsDetails,
      }),
    [releasedScrubberValue.startValue, releasedScrubberValue.endValue]
  );
  const endGraphDateValue = new Date(selectedScrubberValue.endValue);
  endGraphDateValue.setDate(endGraphDateValue.getDate() - 1);

  const diseaseActivityCall = () => {
    onNeedApiGetDiseaseActivity(
      new Date(releasedScrubberValue.startValue),
      new Date(releasedScrubberValue.endValue),
      aggregationCategory
    )
      .then(data => {
        const newGraphData = (data || []).map(
          x =>
            ({
              date: parseIsoDateStartOfDayUtc0(x.startDate),
              dateEnd: parseIsoDateStartOfDayUtc0(x.endDate),
              series1: x.caseCounts?.reportedCases || 0,
              series2: x.caseCounts?.deaths || 0,
              updatesCount: x.totalDescriptionCount, // NOTE: to save response, BE gives only 2 descriptions for preview (see TemporalActivityRequestModel.descriptionCountForAggregationTime: 2)
              updates: x.descriptions?.map(
                d =>
                  ({
                    date: d.reportDate,
                    locationName: d.locationName,
                    text: d.description,
                    isRawHtml: d.hasHtmlTagsInDescription,
                  } as DiseaseActivityDatumUpdate)
              ),
            } as DiseaseActivityDatumType)
        );
        setGraphData(newGraphData);
        setAggregationCategoryState(aggregationCategory);
      })
      .finally(() => {
        setIsGraphLoading(false);
      });
  };

  useEffect(() => {
    diseaseActivityCall();
  }, [releasedScrubberValue, aggregationCategory]);

  return (
    <Card
      fluid
      sx={{
        '&.ui.card': {
          border: sxtheme(t => `1px solid ${t.colors.stone20}`),
        },
      }}
    >
      <Card.Content>
        <Typography variant="h6" color="stone90">
          {labelCardTitle}
        </Typography>
      </Card.Content>
      <Card.Content>
        <div
          sx={{
            display: 'flex',
            flexDirection: 'column-reverse', // CODE: 6b218f23
          }}
        >
          <div
            sx={{
              mx: `-${GraphMarginPopoutHackPx}px`,
              mt: `-${GraphMarginChipsAlignYAxisCutoffHackPx}px`, // CODE: 6b218f23: column-reverse due to z-indexing from negative margin axis hack
            }}
          >
            <BdDiseaseActivityGraph
              graphData={graphData}
              minDate={new Date(selectedScrubberValue.startValue)}
              maxDate={new Date(selectedScrubberValue.endValue)}
              highlightStartDate={temporalWindowStartDate}
              highlightEndDate={temporalWindowEndDate}
              aggregationDays={MapAggregationCat2AggregationDays[aggregationCategoryState]}
              isLoading={isGraphLoading}
              onShowingAverageChange={setShowAverageCurveInLegend}
              renderPopupLower={renderGraphPopupLower}
              renderPopupUpper={renderGraphPopupUpper}
              disablePopupUpper={isMobileDevice}
              onlyWithUpdatesMakeActiveOnClick={!isMobileDevice}
              width={GraphWidth + 2 * GraphMarginPopoutHackPx}
              height={120 + GraphMarginChipsAlignYAxisCutoffHackPx}
              padding={{
                top: GraphMarginChipsAlignYAxisCutoffHackPx,
                left: GraphMarginPopoutHackPx,
                bottom: 16,
                right: GraphPaddingR + GraphMarginPopoutHackPx,
              }}
            />
          </div>
          <div sx={{ mt: '-8px', mb: '10px' }}>
            <DaGraphLegend
              aggregationCategory={aggregationCategoryState}
              showAvg={showAverageCurveInLegend}
            />
          </div>
        </div>
        <div
          sx={{
            width: `${GraphWidth - GraphPaddingR}px`,
            mt: '15px',
            mb: '9px',
          }}
        >
          <BdScrubber
            disabled={scrubberSetup.disabled}
            min={scrubberSetup.min}
            max={scrubberSetup.max}
            minLabel={scrubberSetup.minLabel}
            maxLabel={scrubberSetup.maxLabel}
            defaultValue={selectedScrubberValue}
            onValueChange={setSelectedScrubberValue}
            releaseTrigger={setReleasedScrubberValue}
            tipFormatter={scrubberSetup.tipFormatter}
            minInterval={Date30DaysInMilleseconds}
          />
        </div>
      </Card.Content>
      <BdCardFooterLink
        onClick={() => onGoToActivityHistory()}
        label="See activity history"
        isSelected={isFooterActive}
        testId="DiseaseActivityCard-footerlink"
      />
    </Card>
  );
};

export default DiseaseActivityCard;
